import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { MeasureInvestmentDto, PvEstimateDto, RangeDto, SteckbriefCalculationResultDto } from '../ensan-steckbrief.dto';
import {
    INVESTMENT_NOT_AVAILABLE_KEY,
    formatEuroRange,
    formatInvestmentQuantity,
    formatUnitCostRange,
    isPvEstimateRetryable,
    missingInvestmentInputLabel,
    pvEstimateReasonLabel,
    pvRoofSourceLabel,
} from '../steckbrief-investment';
import { measureLabel } from '../steckbrief-variant-fields';

interface SteckbriefInvestmentBreakdownProps {
    calc: SteckbriefCalculationResultDto;
    /**
     * "Refresh PV estimate" (GEN-799): forces a new PV Calculator computation
     * for the current values — offered on a NOT_AVAILABLE PV line whose reason
     * is retryable. Absent hides the action.
     */
    onRefreshPvEstimate?: () => void;
    /**
     * True while the synchronous refresh (and the recalculation that follows
     * it) is in flight — the action shows a spinner and is disabled.
     */
    pvEstimateRefreshing?: boolean;
}

/**
 * The item-by-item investment estimate behind the two printed totals
 * (GEN-780), rendered inside "Annahmen & abgeleitete Werte": one row per
 * SELECTED measure — Maßnahme · Menge (+ unit per basis; "pauschal" for a
 * flat-rate line) · €/Einheit range · Kosten range · Prio marker — then both
 * totals as the engine returned them. The unit of every line follows the
 * response's `basis` (e.g. Fenstererneuerung per facade m²) — nothing here
 * hard-codes which measure scales with which input.
 * Read-only ops visibility: every number is the `/calculate` response's, the
 * client only labels and locale-formats (ADR-018). A QUANTITY_UNKNOWN row names
 * the missing input; a NOT_PRICED row says there is no cost data; a
 * NOT_AVAILABLE row (GEN-799, the PV line whose PV Calculator estimate failed
 * or is disabled) says so with the reason and is left out of the totals; a
 * PRICED PV row names the roof-data source; an unknown (future) status renders
 * its raw code.
 */
export function SteckbriefInvestmentBreakdown({
    calc,
    onRefreshPvEstimate,
    pvEstimateRefreshing = false,
}: SteckbriefInvestmentBreakdownProps) {
    const { t, i18n } = useTranslation();
    const language = i18n.language;
    // Defensive until the backend GEN-780 PR deploys: absent = no lines.
    const lines = calc.measureInvestments ?? [];
    // GEN-799: the estimate behind the PHOTOVOLTAICS line — absent on an older
    // backend, null when PV is not selected.
    const pvEstimate: PvEstimateDto | null = calc.pvEstimate ?? null;

    function costCell(line: MeasureInvestmentDto) {
        if (line.status === 'PRICED') return formatEuroRange(language, line.cost) ?? '–';
        if (line.status === 'QUANTITY_UNKNOWN') {
            return (
                <Box component="span" sx={{ color: 'warning.main' }}>
                    {t('Missing input: {{input}}', { input: missingInvestmentInputLabel(t, line.basis) })}
                </Box>
            );
        }
        return line.status;
    }

    /**
     * The NOT_AVAILABLE text of the PV line (GEN-799): the feature switched off
     * (local dev), or the calculator failed with the reason in parentheses —
     * plus the refresh action for a retryable reason.
     */
    function notAvailableCell(line: MeasureInvestmentDto) {
        const reason = pvEstimate?.reason ?? null;
        const retryable = isPvEstimateRetryable(reason);
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Box
                    component="span"
                    sx={{ color: 'warning.main' }}
                    data-testid={`investment-unavailable-${line.measure}`}
                >
                    {reason === 'DISABLED'
                        ? t('PV estimate disabled')
                        : reason === null
                          ? t('PV Calculator failed – PV cost not included')
                          : `${t('PV Calculator failed – PV cost not included')} (${pvEstimateReasonLabel(t, reason)})`}
                </Box>
                {retryable && onRefreshPvEstimate && (
                    <Tooltip title={t('Refresh PV estimate')}>
                        {/* span: Tooltip needs an enabled event target even while the button is disabled */}
                        <span>
                            <IconButton
                                size="small"
                                onClick={onRefreshPvEstimate}
                                disabled={pvEstimateRefreshing}
                                aria-label={t('Refresh PV estimate')}
                                data-testid="refresh-pv-estimate"
                            >
                                {pvEstimateRefreshing ? <CircularProgress size={14} /> : <Refresh fontSize="small" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Box>
        );
    }

    /** The roof-data source under a PRICED PV line's label (GEN-799). */
    function sourceNote(line: MeasureInvestmentDto) {
        if (line.measure !== 'PHOTOVOLTAICS' || line.status !== 'PRICED' || !pvEstimate?.roofSource) return null;
        return (
            <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block' }}
                data-testid="investment-pv-source"
            >
                {t('Source: {{source}}', { source: pvRoofSourceLabel(pvEstimate.roofSource) })}
            </Typography>
        );
    }

    function totalText(range: RangeDto | null | undefined): string {
        return formatEuroRange(language, range) ?? t(INVESTMENT_NOT_AVAILABLE_KEY);
    }

    return (
        <Box data-testid="investment-breakdown">
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                {t('Investment costs')}
            </Typography>
            {lines.length === 0 ? (
                <Typography variant="body2" data-testid="investment-breakdown-empty">
                    {t('No measures selected — nothing to price.')}
                </Typography>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small" aria-label={t('Investment costs')}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('Measure')}</TableCell>
                                <TableCell align="right">{t('Quantity')}</TableCell>
                                <TableCell align="right">{t('Cost per unit')}</TableCell>
                                <TableCell align="right">{t('Cost')}</TableCell>
                                <TableCell align="center">{t('Prio')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lines.map((line) => (
                                <TableRow key={line.measure} data-testid={`investment-line-${line.measure}`}>
                                    <TableCell>
                                        {measureLabel(t, line.measure)}
                                        {sourceNote(line)}
                                    </TableCell>
                                    {line.status === 'NOT_PRICED' ? (
                                        // No rate exists (a GEN-777 retired measure on a
                                        // legacy selection) — one honest message instead of
                                        // three dashes.
                                        <TableCell colSpan={3} sx={{ color: 'text.secondary' }}>
                                            {t('No cost data for this measure.')}
                                        </TableCell>
                                    ) : line.status === 'NOT_AVAILABLE' ? (
                                        // GEN-799: the PV Calculator estimate failed or is
                                        // disabled — no cost, the totals leave the line out.
                                        <TableCell colSpan={3}>{notAvailableCell(line)}</TableCell>
                                    ) : (
                                        <>
                                            <TableCell align="right">
                                                {formatInvestmentQuantity(t, language, line.basis, line.quantity)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {formatUnitCostRange(
                                                    language,
                                                    line.basis,
                                                    line.unitCostLow,
                                                    line.unitCostHigh,
                                                )}
                                            </TableCell>
                                            <TableCell align="right">{costCell(line)}</TableCell>
                                        </>
                                    )}
                                    <TableCell align="center">
                                        {line.prio && (
                                            <Chip
                                                size="small"
                                                color="warning"
                                                variant="outlined"
                                                label={t('Prio')}
                                                data-testid={`investment-prio-${line.measure}`}
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            <TableRow data-testid="investment-total">
                                <TableCell colSpan={3} sx={{ fontWeight: 600 }}>
                                    {t('Investment costs (complete renovation)')}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                    {totalText(calc.investmentCostRange)}
                                </TableCell>
                                <TableCell />
                            </TableRow>
                            <TableRow data-testid="investment-priority-total">
                                <TableCell colSpan={3} sx={{ fontWeight: 600 }}>
                                    {t('Investment costs (priority measures)')}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                    {totalText(calc.priorityInvestmentCostRange)}
                                </TableCell>
                                <TableCell />
                            </TableRow>
                        </TableBody>
                    </Table>
                </Box>
            )}
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                {t(
                    'Cost = quantity × unit rate. Totals are rounded to the nearest 100,000 € and only given when every measure of the set has a cost.',
                )}{' '}
                {t('A PV line marked as not available is excluded from the totals.')}
            </Typography>
        </Box>
    );
}
